import React, {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GroupedVirtuoso, GroupedVirtuosoHandle } from 'react-virtuoso-next';
import { useDispatch, useSelector } from 'react-redux';
import { KeyboardArrowDown } from '@material-ui/icons';
import * as Loaders from '~/components/Loaders';
import {
  _,
  dayjs,
  ReactSVG,
  ToastMessage,
  Trans,
  useTranslation,
} from '~/dependencies';
import { Message } from '~/interfaces/conversations';
import { RootState } from '~/store/config';
import * as actions from '~/store/actions';
import { DeleteMessageIcon, NoSearchResultIcon } from '~/assets/svgIcons';
import { colors } from '~/layout';
import { MIN_SEARCH_CARACTERS } from '../..';
import ConfirmationBox from '../../../components/ConfirmationBox';
import Row from '../Row';
import {
  Container,
  Empty,
  ScrollToBottomButton,
  SearchFoundResults,
  Section,
} from './styles';

const groupMessagesByDate = (
  messageList: Record<string, any>,
  userTimeZone: string
) => {
  let messages;

  messages = _(messageList)
    .orderBy((e: Message) => e.createdAt)
    .values();

  messages = messages.map((message: Message) => ({
    ...message,
    createdAt:
      dayjs(message.createdAt)
        .tz(userTimeZone)
        .format('ddd DD MMMM') ===
      dayjs(new Date())
        .tz(userTimeZone)
        .format('ddd DD MMMM')
        ? `Today`
        : `${dayjs(message.createdAt)
            .tz(userTimeZone)
            .format('ddd DD MMMM')}`,
  }));

  return _(messages)
    .groupBy((e: Message) => e.createdAt)
    .value();
};

const EmptyConversation = () => {
  const { t } = useTranslation();

  return (
    <Empty data-test-id="messages-empty-state">
      <ReactSVG src={NoSearchResultIcon} className="icon" />
      <span className="title">{t('screens.conversation.noResultFound')}</span>
      <span className="sub-title ">
        {t('screens.conversation.noResultFoundSubTitle')}
      </span>
    </Empty>
  );
};

type ListProps = {
  data: Record<string, any>;
  searchValue: string;
};

const List = ({ data, searchValue }: ListProps) => {
  // REF's
  const virtuoso = useRef<GroupedVirtuosoHandle>(null);
  const hasScrolledOnLoadRef = useRef(false);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // STATE's
  const [openDialog, setOpenDialog] = useState(false);
  const [messageId, setMessageId] = useState<string>('');
  const [conversationId, setConversationId] = useState<string>('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isRevealed, setIsRevealed] = useState(false);

  // SELECTOR's
  const isLoading = useSelector(
    (state: RootState) => state.conversation?.isLoading
  );
  const userTimeZone = useSelector(
    (state: RootState) => state.auth.userTimeZone
  );

  const groupedMessages = useMemo(
    () => groupMessagesByDate(data, userTimeZone),
    [data, userTimeZone]
  );
  const groupCounts = useMemo(
    () => Object.values(groupedMessages).map(e => (e as Message[]).length),
    [groupedMessages]
  );
  const groups = useMemo(() => Object.keys(groupedMessages), [groupedMessages]);

  const renderGroup = useCallback(
    (index: number) => (
      <Section data-test-id="message-date-separator">
        <div className="line" />
        <span>{groups[index]}</span>
        <div className="line" />
      </Section>
    ),
    [groups]
  );

  const scrollToLastItem = useCallback(
    (behavior: 'auto' | 'smooth' = 'auto') => {
      virtuoso.current?.scrollToIndex({
        index: Math.max(0, (data?.length ?? 0) - 1),
        align: 'end',
        behavior,
      });
    },
    [data]
  );

  /**
   * Callback when the user click on the cancel message
   * Display the confirmation box
   */
  const handleOnClickCancelMessage = useCallback(
    (scheduledMessageInfo: any) => {
      const { messageId: id, conversationId: convId } = scheduledMessageInfo;
      setConversationId(convId);
      setMessageId(id);
      setOpenDialog(true);
    },
    []
  );

  const handleCloseConfirmationBox = useCallback(() => {
    setOpenDialog(false);
    setMessageId('');
  }, []);

  const renderItem = useCallback(
    (index: number) => (
      <Row
        item={data[index]}
        userTz={userTimeZone}
        searchValue={searchValue}
        cancelMessage={handleOnClickCancelMessage}
      />
    ),
    [data, userTimeZone, searchValue, handleOnClickCancelMessage]
  );

  const handleConfirmCancelScheduledMessage = async () => {
    try {
      await dispatch(
        actions.cancelConversationMessage(conversationId, messageId)
      );
    } catch (err) {
      ToastMessage(
        'error',
        t('screens.conversation.failureToCancelMessage'),
        7000
      );
    } finally {
      handleCloseConfirmationBox();
    }
  };

  // Scroll to the last message once, after data first arrives.
  useEffect(() => {
    if (hasScrolledOnLoadRef.current) return undefined;
    if (groupCounts.length === 0) return undefined;
    const id = requestAnimationFrame(() => {
      virtuoso.current?.scrollToIndex({
        index: Math.max(0, data.length - 1),
        align: 'end',
      });
      hasScrolledOnLoadRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [groupCounts.length, data.length]);

  // Hide the list behind a loader for a brief window after the data lands.
  // Virtuoso still mounts (so visible media starts loading), but the user
  // doesn't see / can't interact with the scroll surface until first-paint
  // jank from media decode has settled. Heuristic 300ms.
  useEffect(() => {
    if (isLoading || groupCounts.length === 0 || isRevealed) return undefined;
    const id = setTimeout(() => setIsRevealed(true), 300);
    return () => clearTimeout(id);
  }, [isLoading, groupCounts.length, isRevealed]);

  return (
    <Container>
      <ConfirmationBox
        title={t('screens.conversation.cancelMessageAction')}
        open={openDialog}
        close={handleCloseConfirmationBox}
        icon={DeleteMessageIcon}
        isLoading={isLoading}
        btnColor={colors.yellow04}
        btnName={t('screens.conversation.btnDoIt')}
        handleAction={handleConfirmCancelScheduledMessage}
      />

      {data.length > 0 && searchValue.length > MIN_SEARCH_CARACTERS ? (
        <SearchFoundResults data-test-id="messages-search-results">
          <span>
            <Trans
              i18nKey="screens.conversation.searchLenResult"
              values={{ itemLen: data.length }}
              components={{ bold: <strong className="text-blue-500" /> }}
            />
          </span>
        </SearchFoundResults>
      ) : null}

      {groupCounts.length > 0 ? (
        <GroupedVirtuoso
          ref={virtuoso}
          groupCounts={groupCounts}
          groupContent={renderGroup}
          itemContent={renderItem}
          atBottomStateChange={setIsAtBottom}
          followOutput
          style={{
            flex: 1,
            opacity: isRevealed ? 1 : 0,
            pointerEvents: isRevealed ? 'auto' : 'none',
          }}
        />
      ) : null}
      {groupCounts.length > 0 && !isRevealed ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: colors.white,
            zIndex: 1,
            padding: '0 10px',
          }}>
          <Loaders.List numRows={11} />
        </div>
      ) : null}
      {data.length === 0 && !isLoading ? <EmptyConversation /> : null}
      <ScrollToBottomButton
        type="button"
        aria-label={t('screens.conversation.scrollToBottom', {
          defaultValue: 'Scroll to bottom',
        })}
        visible={!isAtBottom && groupCounts.length > 0}
        onClick={() => scrollToLastItem('smooth')}>
        <KeyboardArrowDown />
      </ScrollToBottomButton>
    </Container>
  );
};

export default List;
